/**
 * ImportProformasDialog — the proforma column vocabulary and copy, wrapped
 * around the shared `GroupedImportDialog`.
 *
 * CSV shape (one row = one line; group by `source-ref`):
 *   Header (repeated per source-ref, first row wins):
 *     source-ref               idempotency key (max 60)
 *     customer-id              UUID (from `/sales/customers` list; must exist)
 *     issued-on                ISO date YYYY-MM-DD
 *     valid-until              ISO date YYYY-MM-DD (optional; quote validity)
 *     place-of-supply          two-digit GSTIN state code
 *     customer-note            (optional; visible to customer)
 *     internal-note            (optional; internal only)
 *   Line (one per CSV row):
 *     description              free text
 *     hsn                      4-8 char HSN/SAC
 *     unit                     Nos, Pcs, KG, etc. (default Nos)
 *     quantity                 decimal (e.g. 1.5) — converted to milli
 *     rate-rupees              per-unit rate in rupees (e.g. 1200.50)
 *     tax-percent              e.g. 18 → bps 1800
 *     discount-percent         default 0 → bps
 *
 * INR only. Non-fiscal: no PRO number allocated, no ledger posting, no
 * receivable. Foreign-currency + non-standard tax treatments require the
 * composer, not import.
 */
import type { proformas as contract } from "@flint/api-contract";
import {
  commonHeaderFields,
  GroupedImportDialog,
  type GroupedImportConfig,
  type GroupedImportRunner,
  type ImportSummary,
} from "../../../components/csv-import/index.ts";
import { trpc } from "../../../lib/trpc.ts";

type ProformaHeader = Omit<contract.ImportRow, "lines" | "sourceRef">;

const HEADER_MAP: Record<string, string> = {
  "source-ref": "sourceRef",
  "customer-id": "customerId",
  "issued-on": "issuedOn",
  "valid-until": "validUntil",
  "place-of-supply": "placeOfSupply",
  "customer-note": "customerNote",
  "internal-note": "internalNote",
  description: "description",
  hsn: "hsn",
  unit: "unit",
  quantity: "_quantity",
  "rate-rupees": "_rateRupees",
  "tax-percent": "_taxPercent",
  "discount-percent": "_discountPercent",
};

const CONFIG: GroupedImportConfig<ProformaHeader> = {
  noun: "proforma",
  nounPlural: "proformas",
  article: "a",
  inputId: "import-proforma-csv",
  dialogDescription: (
    <>
      Up to 500 rows per file (grouped into proformas by <code>source-ref</code>). Non-fiscal:
      imports create drafts only — no PRO number, no ledger posting.
    </>
  ),
  templateHint: (
    <>
      Two example rows show a two-line proforma grouped by <code>PRO-001</code>. Replace{" "}
      <code>{"{customer-uuid}"}</code> with a real customer id from the Customers list. INR +
      CGST/SGST/IGST treatment is auto-derived from place-of-supply; foreign currency and special
      tax treatments require the composer.
    </>
  ),
  schema: {
    headerMap: HEADER_MAP,
    headerLabel: {
      "source-ref": "Source ref (proforma group key)",
      "customer-id": "Customer UUID",
      "issued-on": "Issued on (YYYY-MM-DD)",
      "valid-until": "Valid until (YYYY-MM-DD)",
      "place-of-supply": "Place of supply (state code)",
      "customer-note": "Customer note",
      "internal-note": "Internal note",
      description: "Line description",
      hsn: "HSN/SAC",
      unit: "Unit",
      quantity: "Quantity",
      "rate-rupees": "Rate (₹)",
      "tax-percent": "Tax %",
      "discount-percent": "Discount %",
    },
    headerAliases: {
      "proforma ref": "source-ref",
      "quote ref": "source-ref",
      reference: "source-ref",
      ref: "source-ref",
      customer: "customer-id",
      "customer uuid": "customer-id",
      "customer id": "customer-id",
      date: "issued-on",
      "proforma date": "issued-on",
      "quote date": "issued-on",
      "valid till": "valid-until",
      "expires on": "valid-until",
      "quote validity": "valid-until",
      "place of supply": "place-of-supply",
      pos: "place-of-supply",
      state: "place-of-supply",
      item: "description",
      product: "description",
      "hsn code": "hsn",
      sac: "hsn",
      qty: "quantity",
      rate: "rate-rupees",
      price: "rate-rupees",
      "unit price": "rate-rupees",
      tax: "tax-percent",
      gst: "tax-percent",
      "gst percent": "tax-percent",
      discount: "discount-percent",
    },
    requiredHeaders: [
      "source-ref",
      "customer-id",
      "issued-on",
      "place-of-supply",
      "description",
      "hsn",
      "quantity",
      "rate-rupees",
      "tax-percent",
    ],
  },
  // Column count MUST match the header count exactly.
  exampleRows: [
    "PRO-001,{customer-uuid},2026-07-14,2026-07-29,29,,,Cotton shirt XL,6109,Nos,10,850,18,0",
    "PRO-001,{customer-uuid},2026-07-14,2026-07-29,29,,,Denim trouser 34,6203,Nos,5,1500,18,0",
  ],
  csvFileName: "proformas-import-template.csv",
  xlsx: {
    sheetName: "Proformas",
    title: "Flint · Proforma import template",
    guidance:
      "One row = one line. Group rows into proformas via source-ref (first row wins for header fields). Non-fiscal — creates drafts only.",
    columnWidths: {
      "source-ref": 14,
      "customer-id": 40,
      "issued-on": 13,
      "valid-until": 13,
      "place-of-supply": 16,
      "customer-note": 28,
      "internal-note": 28,
      description: 32,
      hsn: 10,
      unit: 8,
      quantity: 11,
      "rate-rupees": 14,
      "tax-percent": 12,
      "discount-percent": 16,
    },
    textHeaders: new Set([
      "source-ref",
      "customer-id",
      "issued-on",
      "valid-until",
      "place-of-supply",
      "hsn",
    ]),
    fileName: "proformas-import-template.xlsx",
  },
  buildHeader: (raw) => {
    const validUntil = (raw["valid-until"] ?? "").trim();
    return {
      ...commonHeaderFields(raw),
      ...(validUntil ? { expiresOn: validUntil } : {}),
    };
  },
};

function useProformaImport(): GroupedImportRunner<ProformaHeader> {
  const utils = trpc.useUtils();
  const mutation = trpc.proformas.import.useMutation();
  return {
    isPending: mutation.isPending,
    skipped: mutation.data?.skipped,
    run: (rows) => mutation.mutateAsync({ rows }),
    invalidate: () => utils.proformas.list.invalidate(),
  };
}

interface ImportProformasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: (result: ImportSummary) => void;
}

export function ImportProformasDialog({
  open,
  onOpenChange,
  onImported,
}: ImportProformasDialogProps) {
  const runner = useProformaImport();
  return (
    <GroupedImportDialog
      config={CONFIG}
      runner={runner}
      open={open}
      onOpenChange={onOpenChange}
      onImported={onImported}
    />
  );
}
